import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {FormBuilder, Validators} from '@angular/forms';
import {RoleService} from '../../../core/services/role.service';
import {Role} from '../../../core/models/role.model';
import {LoadingService} from '../../../core/services/loading.service';
import {NzMessageService} from 'ng-zorro-antd/message';
import {UserService} from '../../../core/services/user.service';
import {User} from '../../../core/models/user.model';
import {AppUtils} from '../../../core/config/app.utils';
import {ActionAlert} from '../../../core/dto/actionAlert';
import {Location} from '@angular/common';
import {SETTINGS} from '../../../core/config/common.settings';
import { CountryService } from '../../../core/services/country.service';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-add-edit-user',
  templateUrl: './add-edit-user.component.html',
  styleUrls: ['./add-edit-user.component.scss'],
})
export class AddEditUserComponent implements OnInit {
  rId: any = undefined;
  isAdd = false;
  userForm: any;
  dataLoading = false;
  formErrors: any = {
    firstname: '',
    lastname: '',
    email: '',
    mobile: '',
    role: '',
    status: '',
  };
  allRoles: Role[] = [];
  singleUser: User = {};
  actionMessage = new ActionAlert();
  countries: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private roleService: RoleService,
    private userService: UserService,
    private loading: LoadingService,
    private message: NzMessageService,
    private _location: Location,
    private countryService: CountryService,
    private translocoService: TranslocoService) {
  }

  ngOnInit(): void {
    // Load all data
    this.loading.start();
    Promise.all([this.getCountries(), this.loadAllRoles()]).then(() => this.loading.stop());

    // Set Add or Update Page
    this.rId = this.route.snapshot.params['id'];
    this.isAdd = !this.rId;
    if (!this.isAdd) {
      this.loadUserByID().then();
    }

    this.initUserForm();
  }

  initUserForm(): void {
    this.userForm = this.fb.group({
      firstname: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      email: [
        {value: '', disabled: !this.isAdd},
        [Validators.required, Validators.email],
      ],
      mobile: [
        '',
        [Validators.required, Validators.pattern(SETTINGS.REGEX.PHONE.EXP)]
      ],
      dialCode: ['', [Validators.required]],
      role: ['', [Validators.required]],
      active: [true, [Validators.required]],
    });
    this.userForm.valueChanges.subscribe(() => {
      AppUtils.getFormErrors(this.userForm, this.formErrors);
    });
  }

  async loadAllRoles(): Promise<any> {
    this.dataLoading = true;
    try {
      this.allRoles = await this.roleService.getAllRoles();
      this.dataLoading = false;
    } catch (e) {
      this.dataLoading = false;
      this.message.error(this.translocoService.translate('users.addEdit.messages.rolesDidNotLoad'));
      this._location.back();
    }
  }

  async getCountries(): Promise<void> {
    this.countries = await this.countryService.getCountries();
  }

  async loadUserByID(): Promise<any> {
    this.dataLoading = true;
    try {
      this.singleUser = await this.userService.getUserByID(this.rId);
      this.userForm.patchValue(this.singleUser);
      this.dataLoading = false;
    } catch (e) {
      this.dataLoading = false;
      this.message.error(this.translocoService.translate('users.addEdit.messages.userDoesNotExist'));
      this._location.back();
    }
  }

  async addUser(): Promise<any> {
    this.loading.start();
    try {
      await this.userService.addNewUser({
        link: `${location.origin}/login`,
        ...this.userForm.getRawValue(),
      });
      this.actionMessage = new ActionAlert(
        true,
        this.isAdd,
        this.translocoService.translate('users.addEdit.messages.newUserAdded'),
        'success'
      );
      this.userForm.reset({ active: true });
      this.loading.stop();
    } catch (e: any) {
      this.actionMessage = new ActionAlert(
        true,
        this.isAdd,
        e.error.message || this.translocoService.translate('users.addEdit.messages.newUserNotAdded'),
        'error'
      );
      this.loading.stop();
    }
  }

  async editUser(): Promise<any> {
    this.loading.start();
    try {
      const payload: User = {
        _id: this.rId,
        ...this.userForm.getRawValue(),
      };
      await this.userService.editUser(payload);
      this.actionMessage = new ActionAlert(
        true,
        !this.isAdd,
        this.translocoService.translate('users.addEdit.messages.userUpdated'),
        'success'
      );
      this.loading.stop();
    } catch (e: any) {
      this.actionMessage = new ActionAlert(
        true,
        !this.isAdd,
        e.error.message || this.translocoService.translate('users.addEdit.messages.userNotUpdated'),
        'error'
      );
      this.loading.stop();
    }
  }
}
